import { useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { useTownSession } from "../app/TownSessionContext";
import { readJson, writeJson } from "../lib/storage";
import { useTownStore } from "../state/store";

/** Set once this browser has walked (any way) or said no thanks: the invitation does not show here again. */
export const WALK_INVITE_STORAGE_KEY = "townsy.walkInvite.v1";

export interface WalkInviteInput {
  seen: boolean;
  sceneReady: boolean;
  joined: boolean;
  roomKind: "street" | "interior" | null;
  mode: "overview" | "walk";
}

/** In Overview on the street, once the town is built and the visitor's avatar is in it, until they walk or say no. */
export function walkInviteShown(s: WalkInviteInput): boolean {
  return !s.seen && s.sceneReady && s.joined && s.roomKind === "street" && s.mode === "overview";
}

/**
 * ADR-0057 (founder 2026-10-30: "i saw people in the town square, but no one moved around or explored the city ... if
 * its a persons first time coming to the site, there is a little overlay that says 'Click here to walk around and
 * explore'"). The town opens in Overview (founder 2026-10-26), where a new visitor's avatar stands in the town square
 * and the arrow keys move the camera. The invitation switches to Walk; walking by any way (this button, the HUD's Walk)
 * or × answers it for this browser. Storage unavailable: it shows on each load until answered.
 */
export function WalkInvite() {
  const session = useTownSession();
  const state = useTownStore(
    useShallow((s) => ({
      mode: s.ui.mode,
      sceneReady: s.ui.sceneStatus === "ready",
      joined: s.room.status === "joined",
      roomKind: s.room.kind,
      isTouch: s.ui.isTouch,
    })),
  );
  const [seen, setSeen] = useState(() => readJson<boolean>("local", WALK_INVITE_STORAGE_KEY) === true);
  useEffect(() => {
    if (state.mode !== "walk" || seen) return;
    writeJson("local", WALK_INVITE_STORAGE_KEY, true);
    setSeen(true);
  }, [state.mode, seen]);
  if (!session || !walkInviteShown({ seen, ...state })) return null;
  return (
    <WalkInviteView
      touch={state.isTouch}
      onWalk={() => session.setMode("walk")}
      onDismiss={() => {
        writeJson("local", WALK_INVITE_STORAGE_KEY, true);
        setSeen(true);
      }}
    />
  );
}

/** The invitation, presentational (server-rendered in the unit tests). */
export function WalkInviteView({ touch, onWalk, onDismiss }: { touch: boolean; onWalk(): void; onDismiss(): void }) {
  return (
    <div className="hud-bottom walk-invite-layer">
      <div className="walk-invite" role="group" aria-label="Walk around the town" data-testid="walk-invite">
        <button type="button" className="btn btn-primary btn-lg walk-invite-go" onClick={onWalk} data-testid="walk-invite-go">
          <svg className="walk-invite-icon" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
            <circle cx="13.5" cy="4" r="2.2" fill="currentColor" />
            <path
              d="M12.2 7.6 9 9.4 7.6 13M12.2 7.6l-1.4 6 3 2.8 1 5.1M10.8 13.6 8.6 17l-2.9 4M12.2 7.6l2.4 3.4 3 1.2"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>{touch ? "Tap here" : "Click here"} to walk around and explore</span>
        </button>
        <button
          type="button"
          className="btn btn-ghost icon-btn walk-invite-dismiss"
          aria-label="No thanks, keep looking from above"
          title="No thanks, keep looking from above"
          onClick={onDismiss}
          data-testid="walk-invite-dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
}
